/* ===================================================================
   Karban — Check Delivery REDESIGN  ·  check-delivery-redesign.js
   Complete self-contained file — no external dependencies.

   TIMER RULE:
   - installTimerStart is stored in sessionStorage ONCE per browser session
   - It NEVER resets when user enters a new pincode
   - It NEVER resets on page refresh or page navigation
   - It only clears when browser/all tabs close (sessionStorage behavior)
   - If timer has already expired, it stays expired for the whole session

   FIXES APPLIED:
   - Bug 1: id="pv2-timer" → class="pv2-timer-el"; interval now updates
             ALL instances via querySelectorAll (fixes mobile timer)
   - Bug 2: pv2AutoAddInstallation called once outside forEach in
             pv2RestoreSession (prevents double cart-add race condition)
   - Bug 3: clearInterval (via pv2StartCountdownInterval) called once
             outside forEach, not inside it (first interval no longer killed)
   - Bug 4: all install intervals tracked in window._pv2InstallIntervals[]
             so pv2Reset clears every one, not just the last
   - Note:  DOMContentLoaded wires up pv2RestoreSession + pv2BindInstallAdd
   =================================================================== */

/* ============================
   CONSTANTS + STATE
============================ */

var KARBAN_INSTALL_DUR        = 30 * 60;            // 30-min timer (seconds)
var KARBAN_PV2_KEY            = 'karban_pv2';        // pincode data key
var KARBAN_TIMER_KEY          = 'karban_pv2_timer';  // SEPARATE key for timer — never wiped by pv2Reset
var _pv2AddedInstallVariantId = null;

/* ============================
   BRAND LINE ICONS
============================ */

var PV2_SVG = {
  tick:   '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>',
  clock:  '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>',
  clock2: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><circle cx="12" cy="12" r="10"/><polyline points="12 7 12 12 15 14"/></svg>',
  wrench: '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.6 2.6-2.4-2.4 2.6-2.6z"/></svg>',
  plus:   '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>'
};

/* ============================
   TIMER — completely separate from pincode data
   Uses KARBAN_TIMER_KEY so pv2Reset() clearing pincode
   does NOT reset the timer.
============================ */

function pv2InstallStart() {
  var stored = null;
  try { stored = sessionStorage.getItem(KARBAN_TIMER_KEY); } catch (e) {}
  if (stored) return parseInt(stored, 10);
  var ts = Date.now();
  try { sessionStorage.setItem(KARBAN_TIMER_KEY, String(ts)); } catch (e) {}
  return ts;
}

function pv2InstallRemaining() {
  var elapsed = Math.floor((Date.now() - pv2InstallStart()) / 1000);
  return Math.max(0, KARBAN_INSTALL_DUR - elapsed);
}

/* ============================
   PINCODE SESSION (separate key)
   pv2Reset clears KARBAN_PV2_KEY only — timer is untouched
============================ */

function pv2SessionGet() {
  try { return JSON.parse(sessionStorage.getItem(KARBAN_PV2_KEY) || '{}'); } catch (e) { return {}; }
}

function pv2SessionSet(obj) {
  try { sessionStorage.setItem(KARBAN_PV2_KEY, JSON.stringify(obj)); } catch (e) {}
}

function pv2SessionClear() {
  // Only clears pincode data — timer key is intentionally NOT touched
  try { sessionStorage.removeItem(KARBAN_PV2_KEY); } catch (e) {}
}

/* ============================
   SAVE SESSION
============================ */

function pv2SaveSession(pin, area, po) {
  pv2SessionSet({
    pin:     pin,
    area:    area,
    po:      po ? JSON.stringify(po) : null,
    savedAt: Date.now()
    // NO installTimerStart here — timer lives in its own key
  });
  // Ensure timer has started (idempotent — only sets once per session)
  pv2InstallStart();
}

/* ============================
   DELIVERY DATE + COUNTDOWN
============================ */

function pv2GetDeliveryDate() {
  var now = new Date();
  var count = 0, d = new Date(now);
  while (count < 5) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0 && d.getDay() !== 6) count++;
  }
  var days   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  var months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return days[d.getDay()] + ', ' + d.getDate() + ' ' + months[d.getMonth()];
}

function pv2GetCountdown() {
  var now    = new Date();
  var cutoff = new Date(now);
  cutoff.setUTCHours(12, 30, 0, 0); // 6 PM IST
  if (now >= cutoff) cutoff.setDate(cutoff.getDate() + 1);
  var diff = Math.max(0, Math.floor((cutoff - now) / 1000));
  return { h: Math.floor(diff / 3600), m: Math.floor((diff % 3600) / 60) };
}

/* ============================
   INSTALL ELIGIBILITY
============================ */

function pv2GetInstallEligibility(card, pin, po) {
  var locations = (card.dataset.installLocations || '').toLowerCase();
  if (!locations) return false;
  var list     = locations.split(',').map(function (s) { return s.trim(); });
  var pinStr   = String(pin || '').toLowerCase();
  var district = ((po && po.District) || '').toLowerCase();
  var state    = ((po && po.State)    || '').toLowerCase();
  var name     = ((po && po.Name)     || '').toLowerCase();
  return list.some(function (loc) {
    return pinStr === loc
      || district.includes(loc)
      || state.includes(loc)
      || name.includes(loc)
      || (district && loc.includes(district));
  });
}

/* ============================
   BUILD RESULT HTML
   FIX Bug 1: id="pv2-timer" → class="pv2-timer-el"
              Duplicate IDs broke mobile; class lets querySelectorAll
              update every instance simultaneously.
   NOTE:      id="pv2-install" removed — was duplicated across desktop
              and mobile renders and is not queried by any JS.
============================ */

function pv2BuildResultHTML(area, showInstall) {
  var date = pv2GetDeliveryDate();
  var cd   = pv2GetCountdown();

  var installHTML = '';
  if (showInstall) {
    installHTML = ''
      + '<div class="pv2-install-card">'
      +   '<div class="pv2-install-top">'
      +     '<span class="pv2-install-icon">' + PV2_SVG.wrench + '</span>'
      +     '<span class="pv2-install-title">Free professional installation'
      +       '<span class="pv2-old-price">&#8377;899</span>'
      +       '<span class="pv2-free-badge">Free</span>'
      +     '</span>'
      +   '</div>'
      +   '<div class="pv2-install-desc">A Karban-certified technician mounts, pairs and tests your Airzone &#8212; we call to book a slot.</div>'
      +   '<div class="pv2-install-act">'
      +     '<div class="pv2-timerchip">'
      +       PV2_SVG.clock2
      +       '<span class="pv2-tlbl">Free install held for</span>'
      +       '<span class="pv2-install-timer">--:--</span>'
      +     '</div>'
      +     '<button type="button" class="pv2-install-add">' + PV2_SVG.plus + 'Add</button>'
      +   '</div>'
      + '</div>';
  }

  return ''
    + '<div class="pv2-confirmed-row">'
    +   '<span class="pv2-confirmed-check">' + PV2_SVG.tick + '</span>'
    +   '<span class="pv2-confirmed-text">Delivering to <strong>' + area + '</strong></span>'
    +   '<button class="pv2-change-btn" data-action="pv2-reset">Change</button>'
    + '</div>'
    + '<div class="pv2-divider"></div>'
    + '<div class="pv2-tags">'
    +   '<span class="pv2-tag">Free delivery</span>'
    +   '<span class="pv2-tag">In stock</span>'
    + '</div>'
    + '<div class="pv2-arrives">Arrives by ' + date + '</div>'
    + '<div class="pv2-countdown">'
    +   PV2_SVG.clock
    // FIX Bug 1: class="pv2-timer-el" instead of id="pv2-timer"
    +   ' Order within <strong class="pv2-timer-el">' + cd.h + 'h ' + cd.m + 'm</strong> for the fastest dispatch'
    + '</div>'
    + installHTML;
}

/* ============================
   SHARED DISPATCH COUNTDOWN INTERVAL
   FIX Bug 1: querySelectorAll updates EVERY .pv2-timer-el (desktop + mobile)
   FIX Bug 3: called once from pv2ShowSuccess / pv2RestoreSession,
              NOT inside a loop — clears existing interval first internally
============================ */

function pv2StartCountdownInterval() {
  clearInterval(window._pv2CountdownInterval);
  window._pv2CountdownInterval = setInterval(function () {
    var els = document.querySelectorAll('.pv2-timer-el');
    if (!els.length) { clearInterval(window._pv2CountdownInterval); return; }
    var c = pv2GetCountdown();
    els.forEach(function (el) { el.textContent = c.h + 'h ' + c.m + 'm'; });
  }, 60000);
}

/* ============================
   INSTALL COUNTDOWN TICKER
   scopeEl = the result div that contains .pv2-install-timer
   Reads pv2InstallRemaining() live — so page refresh / new pincode
   picks up exactly where the timer left off.
   FIX Bug 4: every new interval ID is pushed to window._pv2InstallIntervals[]
              so pv2Reset() can clear ALL of them, not just the last one.
============================ */

function pv2StartInstallCountdown(scopeEl) {
  // Clear any existing ticker on this element
  if (scopeEl._installInterval) clearInterval(scopeEl._installInterval);

  function render() {
    var t = scopeEl.querySelector('.pv2-install-timer');
    if (!t) { clearInterval(scopeEl._installInterval); return; }

    var remaining = pv2InstallRemaining(); // always reads live from sessionStorage

    if (remaining <= 0) {
      t.textContent = '00:00';
      var band = scopeEl.querySelector('.pv2-install-card');
      if (band) band.classList.add('is-expired');
      var lbl = scopeEl.querySelector('.pv2-tlbl');
      if (lbl) lbl.textContent = 'Free install — call us to claim';
      clearInterval(scopeEl._installInterval);
      return;
    }

    var mm = Math.floor(remaining / 60);
    var ss = remaining % 60;
    t.textContent = mm + ':' + String(ss).padStart(2, '0');
  }

  render(); // show immediately, don't wait 1 second
  scopeEl._installInterval = setInterval(render, 1000);

  // FIX Bug 4: track every interval so pv2Reset can clear all of them
  if (!window._pv2InstallIntervals) window._pv2InstallIntervals = [];
  window._pv2InstallIntervals.push(scopeEl._installInterval);
}

/* ============================
   SHOW SUCCESS
============================ */

function pv2ShowSuccess(resultDiv, area, pin, po) {
  var card        = resultDiv.closest('.pincode-v2-card');
  var showInstall = card
    && card.dataset.showInstall === 'true'
    && pv2GetInstallEligibility(card, pin, po);

  resultDiv.innerHTML = pv2BuildResultHTML(area, showInstall);

  // FIX Bug 1 + Bug 3: single call updates all .pv2-timer-el instances
  pv2StartCountdownInterval();

  if (showInstall) {
    pv2StartInstallCountdown(resultDiv);
    pv2AutoAddInstallation(card, true);
  }
}

/* ============================
   RESTORE SESSION (call on every page)
   FIX Bug 2: autoAddCard tracked across the loop; pv2AutoAddInstallation
              called ONCE after the loop — prevents two simultaneous
              /cart.js fetches both seeing the item absent and double-adding.
   FIX Bug 3: pv2StartCountdownInterval() called ONCE after the loop,
              not inside it — first interval is no longer killed by the
              second iteration.
============================ */

function pv2RestoreSession() {
  var data = pv2SessionGet();
  var pin  = data.pin;
  var area = data.area;
  if (!pin || !area) return;

  var po = null;
  try { po = data.po ? JSON.parse(data.po) : null; } catch (e) {}

  // Fill inputs
  ['pv2-pincode-input', 'pv2-pincode-input-mobile'].forEach(function (id) {
    var el = document.getElementById(id);
    if (el) el.value = pin;
  });

  // Rebuild result widgets
  // FIX Bug 2: track card reference; call autoAdd once after loop
  var autoAddCard = null;

  ['pv2-result', 'pv2-result-mobile'].forEach(function (id) {
    var resultDiv = document.getElementById(id);
    if (!resultDiv) return;

    var card        = resultDiv.closest('.pincode-v2-card');
    var showInstall = card
      && card.dataset.showInstall === 'true'
      && pv2GetInstallEligibility(card, pin, po);

    resultDiv.innerHTML = pv2BuildResultHTML(area, showInstall);

    if (showInstall) {
      pv2StartInstallCountdown(resultDiv);
      autoAddCard = card; // FIX Bug 2: save, do NOT call pv2AutoAddInstallation here
    }
  });

  // FIX Bug 1 + Bug 3: one interval, started after both divs are built
  pv2StartCountdownInterval();

  // FIX Bug 2: single autoAdd call — avoids the race that double-adds to cart
  if (autoAddCard) pv2AutoAddInstallation(autoAddCard, true);
}

/* ============================
   RESET — clears pincode only, NOT the timer
   FIX Bug 4: (window._pv2InstallIntervals || []).forEach(clearInterval)
              clears every install interval, not just the last one
============================ */

function pv2Reset() {
  if (_pv2AddedInstallVariantId) {
    pv2RemoveInstallation(_pv2AddedInstallVariantId);
    _pv2AddedInstallVariantId = null;
  }

  pv2SessionClear(); // clears KARBAN_PV2_KEY only — KARBAN_TIMER_KEY survives

  ['pv2-pincode-input', 'pv2-pincode-input-mobile'].forEach(function (id) {
    var el = document.getElementById(id);
    if (el) { el.value = ''; el.focus(); }
  });

  ['pv2-result', 'pv2-result-mobile'].forEach(function (id) {
    var el = document.getElementById(id);
    if (el) el.innerHTML = '';
  });

  clearInterval(window._pv2CountdownInterval);

  // FIX Bug 4: clear ALL tracked install intervals, not just the last one
  (window._pv2InstallIntervals || []).forEach(clearInterval);
  window._pv2InstallIntervals = [];
}
window.pv2Reset = pv2Reset;

/* ============================
   "+ Add" INSTALL BUTTON
============================ */

function pv2BindInstallAdd() {
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.pv2-install-add');
    if (!btn || btn._busy) return;

    btn._busy     = true;
    var original  = btn.innerHTML;
    btn.innerHTML = 'Adding\u2026';

    var idInput     = document.querySelector('input[name="id"]');
    var cartBtn     = document.querySelector('.cart-btn');
    var prodVariant = parseInt(
      (idInput && idInput.value) || (cartBtn && cartBtn.dataset.variantId) || 0
    );
    var qty = parseInt((document.getElementById('quantity') || {}).value || 1) || 1;

    var card           = btn.closest('.pincode-v2-card');
    var installVariant = parseInt(card && card.dataset.installVariantId);

    // STEP 1: check cart first — installation already added hai ya nahi
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var installAlreadyInCart = installVariant
          ? (cart.items || []).some(function (i) { return i.variant_id === installVariant; })
          : false;

        var items = [];
        if (prodVariant) items.push({ id: prodVariant, quantity: qty });

        // Sirf tab add karo jab already cart me NA ho
        if (installVariant && !installAlreadyInCart) {
          items.push({ id: installVariant, quantity: 1 });
        }
        if (installVariant && installAlreadyInCart) {
          _pv2AddedInstallVariantId = installVariant; // state sync kar do
        }

        if (!items.length) {
          btn.innerHTML = original;
          btn._busy = false;
          return Promise.reject('skip'); // chain ko clean tarike se rok do
        }

        return fetch('/cart/add.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ items: items })
        }).then(function (r) {
          if (!r.ok) throw new Error('Cart add failed: ' + r.status);
          if (installVariant && !installAlreadyInCart) _pv2AddedInstallVariantId = installVariant;
          return fetch(window.location.pathname + '?section_id=cart-drawer');
        });
      })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var nd  = doc.querySelector('cart-drawer');
        var od  = document.querySelector('cart-drawer');
        if (od && nd) { od.replaceWith(nd); if (typeof nd.open === 'function') nd.open(); }
        btn.classList.add('is-added');
        btn.innerHTML = 'Added to cart';
      })
      .catch(function (err) {
        if (err === 'skip') return; // already handled above
        console.error('[Karban] pv2BindInstallAdd failed:', err);
        btn.innerHTML = original;
      })
      .finally(function () { btn._busy = false; });
  });
}

/* ============================
   CART HELPERS
============================ */

function pv2AutoAddInstallation(card, forceCheck) {
  var variantId = parseInt(card && card.dataset.installVariantId);
  if (!variantId) return;

  function doAdd() {
    fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ id: variantId, quantity: 1 })
    })
      .then(function () { _pv2AddedInstallVariantId = variantId; })
      .catch(function (e) { console.warn('[Karban] autoAdd failed:', e); });
  }

  if (forceCheck) {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var inCart = (cart.items || []).some(function (i) { return i.variant_id === variantId; });
        if (inCart) { _pv2AddedInstallVariantId = variantId; }
        else        { _pv2AddedInstallVariantId = null; doAdd(); }
      })
      .catch(doAdd);
  } else {
    if (_pv2AddedInstallVariantId === variantId) return;
    doAdd();
  }
}

function pv2RemoveInstallation(variantId) {
  fetch('/cart.js')
    .then(function (r) { return r.json(); })
    .then(function (cart) {
      var item = (cart.items || []).find(function (i) { return i.variant_id === variantId; });
      if (!item) return;
      var updates = {};
      updates[String(variantId)] = 0;
      return fetch('/cart/update.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ updates: updates })
      });
    })
    .catch(function (e) { console.warn('[Karban] removeInstallation failed:', e); });
}

/* ============================
   INIT
   FIX Note: pv2RestoreSession + pv2BindInstallAdd were never called.
   If your Liquid template already calls these, remove this block.
============================ */

document.addEventListener('DOMContentLoaded', function () {
  pv2RestoreSession();
  pv2BindInstallAdd();
});